import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import MenuIcon from '@mui/icons-material/Menu'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Tabs from '@mui/material/Tabs'
import { useNavigate, Link } from 'react-router-dom'
import { useGlobalStore } from '../../../services/store/store'
import {
	StyledBox,
	StyledBoxWithSeparator,
	StyledAppBar,
	StyledToolBar,
	StyledTab
} from './components'
import OpsotTypography from '../../ui/OpsotTypography/index'
import {
	OpsotHomeIcon,
	OpsotSearchIcon,
	OpsotToolkitIcon,
	OpsotExitIcon,
	OpsotXIcon,
	OpsotLinkedInIcon,
	OpsotFacebookIcon,
	OpsotLogoInverse
} from '../assets'
import { isExternalUrl } from '../../../utils'
import { ExitModal } from '../ExitModal/index'
import OpsotLogo from '../../../assets/images/college-of-policing-white-type-logo.png'
import { focusStyle } from '../../../styles/common'

const TopNav = () => {
	const theme = useTheme()
	const isTabletOrGreater = useMediaQuery(theme.breakpoints.up('sm'))
	const navigate = useNavigate()
	const isAdmin = useGlobalStore((state) => state.isAdmin)

	const navigateTo = (to) => {
		navigate(to)
	}
	const [isOpen, setIsOpen] = useState(false)
	const [isExitOpen, setIsExitOpen] = useState(false)
	const userToken = useGlobalStore((state) => state.userToken)
	const currentRoute = useGlobalStore((state) => state.currentRoute)

	const tabItems = useMemo(() => {
		const items = []

		if (userToken) {
			items.push(
				{
					name: 'Home',
					link: '/',
					icon: <OpsotHomeIcon />
				},
				{
					name: 'Search',
					link: '/search',
					icon: <OpsotSearchIcon />
				},
				{
					name: 'Toolkits',
					link: '/toolkits',
					icon: <OpsotToolkitIcon />
				}
			)
		}

		items.push({
			name: 'Back to College of Policing',
			link: 'https://www.college.police.uk/',
			icon: <OpsotExitIcon />,
			style: {
				backgroundColor: 'primary.main',
				color: 'white',
				'&:hover': { color: 'white' }
			}
		})

		return items
	}, [userToken])

	const [currentLink, setCurrentLink] = useState(
		(tabItems.some((item) => item.link === window.location.pathname) &&
			window.location.pathname) ||
			false
	)
	const anchorEl = useRef(null)

	useEffect(() => {
		setCurrentLink(
			(tabItems.some((item) => item.link === window.location.pathname) &&
				window.location.pathname) ||
				false
		)
	}, [tabItems, currentRoute])

	const handleChange = (link) => {
		if (isExternalUrl(link)) {
			if (!isExitOpen) {
				setIsExitOpen(true)
				return
			}
			window.location.href = link
			return
		}
		setCurrentLink(link)
		navigateTo(link)
	}

	const menuItems = [
		...(isAdmin ? [{ name: 'Draft toolkits', link: '/draft-toolkits' }] : []),
		{
			name: 'Privacy policy',
			link: '/privacy-policy'
		},
		{
			name: 'Copyright statement',
			link: '/copyright-statement'
		},
		{
			name: 'Cookie policy',
			link: '/cookie-policy'
		},
		{
			name: 'Accessibility statement',
			link: '/accessibility-statement'
		},
		{
			name: 'Content disclaimer',
			link: '/content-disclaimer'
		},
		{
			name: 'Feedback',
			link: '#',
			onClick: () =>
				window.open('https://www.college.police.uk/contact', '_blank')
		}
	]
	const menuItemsLength = menuItems.length

	const calculateHeight = () => {
		if (isTabletOrGreater) return 'calc(100vh - 248px)'
		return 'calc(100vh - 158px)'
	}

	// elevation removes box shadow
	return (
		<StyledBox>
			<StyledAppBar position="fixed" elevation={4}>
				<StyledToolBar ref={anchorEl}>
					<Box
						sx={{
							display: 'flex',
							margin: '30px 0',
							flexFlow: 'row nowrap',
							flexGrow: 1,
							flexBasis: '100%',
							'& a': {
								...focusStyle
							}
						}}
					>
						<StyledBoxWithSeparator
							sx={{
								display: 'flex',
								alignItems: 'center',
								cursor: 'pointer'
							}}
						>
							<a
								href="https://www.college.police.uk"
								target="_blank"
								rel="noreferrer"
							>
								<img
									src={OpsotLogo}
									alt="College Of Policing"
									width={isTabletOrGreater ? 176 : 90}
								/>
							</a>
						</StyledBoxWithSeparator>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								marginInlineEnd: { xs: 0, md: '1rem' }
							}}
						>
							<Link style={{ textDecoration: 'none' }} to="/">
								<OpsotTypography
									variant={
										isTabletOrGreater ? 'desktopHeadline' : 'responsiveHeadline'
									}
									content="National operating model"
									sx={{ margin: 0 }}
								/>
							</Link>
						</Box>
					</Box>
					<Box sx={{ display: { xs: 'none', md: 'flex', padding: '1rem 0' } }}>
						<Tabs
							aria-label="Top navigation tabs"
							value={currentLink}
							scrollButtons={false}
							sx={{
								'& .MuiTabs-flexContainer': { gap: '1rem' },
								'& .MuiTabs-indicator': { display: 'none' }
							}}
						>
							{tabItems.map((item) => (
								<StyledTab
									key={item.name}
									label={item.name}
									icon={item.icon}
									value={item.link}
									sx={{
										...item.style,
										fontWeight: 400
									}}
									onClick={() => handleChange(item.link)}
								/>
							))}
						</Tabs>
					</Box>

					<IconButton
						size="large"
						edge="end"
						color="inherit"
						disableRipple
						aria-label="menu"
						sx={{
							display: { md: 'none' },
							paddingInlineStart: 0
						}}
						onClick={() => setIsOpen(!isOpen)}
					>
						{isOpen ? (
							<CloseIcon
								style={{ fontSize: isTabletOrGreater ? '40px' : '28px' }}
							/>
						) : (
							<MenuIcon
								style={{ fontSize: isTabletOrGreater ? '40px' : '28px' }}
							/>
						)}
						<Menu
							open={isOpen}
							anchorEl={anchorEl.current}
							marginThreshold={0}
							elevation={0}
							sx={{
								'.MuiList-root': {
									display: 'flex!important',
									flexDirection: 'column'
								},
								'.MuiList-padding': { padding: 0 },
								'.MuiMenuItem-root': { padding: 0 },
								'& .Mui-selected': { textDecoration: 'underline' }
							}}
							transitionDuration={0}
							slotProps={{
								paper: {
									square: true,
									style: {
										backgroundColor: theme.palette.primary.main,
										color: theme.palette.common.white,
										display: 'flex',
										gap: '1.75rem', // 28px
										left: 0,
										maxWidth: '100%',
										padding: isTabletOrGreater ? '1.5rem' : '1rem 0.9375rem', // 16px 15px
										right: 0,
										width: '100%',
										height: calculateHeight()
									}
								}
							}}
						>
							{menuItems.map((item, index) => (
								<MenuItem
									key={item.name}
									selected={item.link === currentLink}
									onClick={() =>
										item.onClick ? item.onClick() : handleChange(item.link)
									}
									sx={{
										flexGrow: index === menuItemsLength - 1 ? 1 : 0,
										flexShrink: index === menuItemsLength - 1 ? 0 : 1,
										flexBasis: index === menuItemsLength - 1 ? '10%' : 0,
										display: index === menuItemsLength - 1 ? 'flex' : 'block',
										alignItems:
											index === menuItemsLength - 1 ? 'flex-start' : 'auto'
									}}
								>
									{item.name}
								</MenuItem>
							))}

							<MenuItem
								sx={{
									display: 'flex',
									gap: '15px',
									justifyContentSelf: 'flex-end'
								}}
								key="social"
							>
								<Link
									target="_blank"
									rel="noreferrer"
									to="https://x.com/collegeofpolice"
								>
									<OpsotXIcon />
								</Link>
								<Link
									target="_blank"
									to="https://www.linkedin.com/company/college-of-policing"
								>
									<OpsotLinkedInIcon />
								</Link>
								<Link
									target="_blank"
									rel="noreferrer"
									to="https://www.facebook.com/CollegeOfPolicing"
								>
									<OpsotFacebookIcon />
								</Link>
							</MenuItem>
							<MenuItem>
								<a
									href="https://www.college.police.uk"
									target="_blank"
									rel="noreferrer"
								>
									<OpsotLogoInverse width="187px" height="68px" />
								</a>
							</MenuItem>
							<MenuItem sx={{ whiteSpace: 'normal' }}>
								&copy; College of Policing Ltd. All content (excluding logos,
								videos, photographs and where otherwise stated) is available for
								re-use under the Non-Commercial College Licence. (
								{new Date().getFullYear()}).
							</MenuItem>
						</Menu>
					</IconButton>
				</StyledToolBar>
				<ExitModal
					isExitOpen={isExitOpen}
					setIsExitOpen={setIsExitOpen}
					handleChange={handleChange}
				/>
			</StyledAppBar>
		</StyledBox>
	)
}

export default TopNav
